import React, { useState, useRef, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  Popover,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../../actions/actions";
import axiosInstance from "../../../../axios/axiosInstance";
import InnerPageheading from "../../../../components/InnerPageheading";
import CustomizedSnackbars from "../../../../components/Toast";
import FinishTimeEntryHeaderBox from "./FinishTimeHeaderBox";
import StartTime from "./StartTime";
import SessionNotesTab from "./SessionNotesTab";
import StartTimeConfirmModal from "./StartTimeConfirmModal";
import FinishTimeBody from "./FinishTimeBody";
import { isUnknownSail } from "./constants";
import { LiveClock } from "./StyledComponents";
import { encrypt } from "../../../global/helperFunctions";
import { CheckCircleRounded } from "@mui/icons-material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const TIME_PENALTIES = [
  "ARB",
  "ENP",
  "ESP",
  "SCP",
  "ZFP",
  "RDG",
  "OCS",
  "DNF",
  "RET",
  "DSQ",
  "BFD",
  "UFD",
];
const isNumeric = (v) => /^\d+$/.test(String(v ?? "").trim());

// ─── Toolbar iOS-style switch (On Water / SN Alpha) ──────────────────────────
const IOSSwitch = styled(Switch)(() => ({
  width: 34,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--ty-primary-color, #356dad)",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 16, height: 16 },
  "& .MuiSwitch-track": {
    borderRadius: 20,
    backgroundColor: "#E9E9EA",
    opacity: 1,
  },
}));

function ToolbarSwitch({ label, checked, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} title={label}>
      <IOSSwitch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
}


export default function NewFinishTimesPage() {
  const dispatch = useDispatch();
  const compactTablet = useMediaQuery("(max-width:1024px)"); // tablet & below → icon-only buttons

  //For Browser refresh alert
  React.useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      // // Older browsers may need this
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Ensure club data is in Redux (normally loaded by sidebar)
  // Required so FinishTimeEntryHeaderBox can fetch the event list
  React.useEffect(() => {
    dispatch(actions.fetchClubData());
  }, []);

  const [raceSession, setRaceSession] = useState(0);
  const [isOnRace, setIsOnRace] = useState(false);
  const [isPlace, setIsPlace] = useState(false);
  const [caseOTBEntrant, setCaseOTBEntrant] = useState(false);
  const [allPenalties, setAllPenalties] = useState([]);
  const [eventId, setEventId] = useState("");
  const [sessionDate, setSessionDate] = useState("");
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ text: "", severity: "" });

  // New-UI specific state
  const [mode, setMode] = useState("manual"); // "real-time" | "manual"
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPanel, setDrawerPanel] = useState("start"); // "start" | "notes"
  // Bumped on every drawer open → remounts the StartTime panel so unsaved
  // typed times are discarded (old screen's tab remounted on each visit).
  const [drawerSeq, setDrawerSeq] = useState(0);
  const [fleetDetails, setFleetDetails] = useState([]);
  const [racePayload, setRacePayload] = useState({
    raceSessionsDetailList: [],
  });
  // Session whose start times have finished loading — the ?tab=start deep link
  // waits on this so the Start Time drawer never opens on empty data.
  const [fleetDetailsSession, setFleetDetailsSession] = useState(0);
  const [processFlag, setProcessFlag] = useState(false); // results being processed → lock UI
  const [confirmOpen, setConfirmOpen] = useState(false); // "Confirm Start Times" dialog before processing
  const [onWater, setOnWater] = useState(false);
  const [snWithAlpha, setSnWithAlpha] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false); // grid data loading on event/date/session change
  const loaderHideTimer = useRef(null);

  const fileInputRef = useRef(null);
  const finishedGridRef = useRef(null); // shared with FinishTimeBody to read sorted display order for CSV export
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const closeImportPopup = () => {
    setImportAnchorEl(null);
    setSelectedFile(null);
  };

  const handleImportSelected = () => {
    if (!selectedFile) return;
    // Lock the page (reuses the same overlay as a session/date switch) for
    // the whole import — parsing + the save round-trip — so the user can't
    // click Process Results or anything else while rows are still landing.
    setSessionLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await importCsvText(String(ev.target?.result || ""));
      } finally {
        setSessionLoading(false);
      }
    };
    reader.onerror = () => setSessionLoading(false);
    reader.readAsText(selectedFile);
    closeImportPopup();
  };

  // CSS pseudo-fullscreen (fixed overlay) — avoids the native Fullscreen API,
  // which iPadOS Safari drops on a scroll/touch gesture.
  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 60); // FinishTimeBody re-measures grid height
  };

  // Escape exits the (CSS pseudo-) fullscreen. Guarded so that pressing Escape
  // while editing a field cancels that edit first instead of also leaving
  // fullscreen on the same keystroke.
  React.useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      const t = e.target;
      if (
        t &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)
      )
        return;
      setIsFullscreen(false);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 60);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen]);

  // Document360 launcher (#document360-widget-iframe, global widget mounted in
  // the sidebar): on this page pin it to the top of the viewport instead of the
  // bottom, and hide it entirely while in pseudo-fullscreen. Done via body
  // classes + CSS in finish-times-new.scss so the rest of the app is untouched.
  React.useEffect(() => {
    document.body.classList.add("ftn-doc360-top");
    return () =>
      document.body.classList.remove("ftn-doc360-top", "ftn-doc360-hidden");
  }, []);
  React.useEffect(() => {
    document.body.classList.toggle(
      "ftn-doc360-hidden",
      isFullscreen || drawerOpen,
    );
  }, [isFullscreen, drawerOpen]);

  // Live clock is a real-time capture aid — show it (in the heading, or the
  // fullscreen toolbar) only in real-time mode for a selected, non-place session.
  const showClock =
    raceSession > 0 && !isPlace && mode === "real-time" && !processFlag;

  // Redux entrant lists (used by CSV import)
  const unfinishedEntrants = useSelector(
    (s) => s.initialFinishRaceEntrantsList.initialFinishRaceEntrantsList.data,
  );
  const completeFinishRaceEntrants = useSelector(
    (s) => s.completeFinishRaceEntrantsList.completeFinishRaceEntrantsList.data,
  );
  const aboutFinishRaceEntrants = useSelector(
    (s) => s.aboutFinishRaceEntrantsList.aboutFinishRaceEntrantsList.data,
  );

  // ── Snackbar helper ─────────────────────────────────────────────────────────
  const handleSnackbarChange = useCallback((text, severity, show) => {
    setSnackbar({ text, severity });
    setOpen(show);
  }, []);

  // ── Load session entrants into Redux (same logic as RaceManager) ─────────────
  const loadSessionEntrants = async (sessionId) => {
    if (!sessionId || sessionId <= 0) return;
    if (loaderHideTimer.current) clearTimeout(loaderHideTimer.current);
    setSessionLoading(true);
    const encryptedSessionId = encodeURIComponent(
      encrypt(JSON.stringify(sessionId)),
    );

    try {
      const PREFINISH_API =
        "api/clubs/FinishTime/prefinish?raceSession=" + encryptedSessionId;
      const response = await axiosInstance.get(PREFINISH_API);

      if (!response.data.data || response.data.data.length === 0) {
        // No pre-existing data → load raw entrants
        const ENTRANTS_API = `api/clubs/FinishTime/getEntrants?raceSession=${encryptedSessionId}`;
        const { data } = await axiosInstance.get(ENTRANTS_API);
        const mappedEntrants = (data.data || []).map((obj) => ({
          ...obj,
          editable: false,
          place: obj.place === 0 ? "" : obj.place,
        }));
        dispatch(actions.updateInitialFinishRaceEntrants(mappedEntrants));
        dispatch(actions.updateAboutFinishRaceEntrants([]));
        dispatch(actions.updateCompleteFinishRaceEntrants([]));
      } else {
        const preFinished = response.data.data
          .filter((x) => x.status === 1)
          .map((obj) => ({
            ...obj,
            editable: obj.editable === 1 ? true : false,
            place: obj.place === 0 ? "" : obj.place,
          }));

        const finishedRaw = response.data.data.filter((x) => x.status === 2);
        // Old-screen parity: prefer the DB-saved position when present. Only
        // when it's empty/unset (null/undefined/""/0) — e.g. a boat sent
        // straight from Unfinished/About-to-Finish that never got a saved
        // position — do we assign one, simply as the highest position seen
        // so far + 1 (not an index/length formula, so adding or removing
        // unrelated boats can never shift an already-assigned number).
        let maxPosition = finishedRaw.reduce((max, obj) => {
          const p = Number(obj.position);
          return obj.position !== null && obj.position !== undefined && obj.position !== "" && p !== 0 && p > max
            ? p
            : max;
        }, 0);
        const finished = finishedRaw
          .map((obj) => {
            const hasValidPosition =
              obj.position !== null && obj.position !== undefined && obj.position !== "" && Number(obj.position) !== 0;
            return {
              ...obj,
              editable: obj.editable === 1 ? true : false,
              place: obj.place === 0 ? "" : obj.place,
              position: hasValidPosition ? Number(obj.position) : ++maxPosition,
            };
          })
          // The backend's ORDER BY (finish time DESC) isn't reliable once
          // placeholder (00:00:00) times are involved, so re-sort explicitly
          // here by the resolved position, descending — highest/most-recent
          // finisher first, matching the grid's newest-on-top convention.
          .sort((a, b) => b.position - a.position);

        const unfinished = response.data.data
          .filter((x) => x.status === 0)
          .map((obj) => ({
            ...obj,
            editable: false,
            place: obj.place === 0 ? "" : obj.place,
          }));

        dispatch(actions.updateInitialFinishRaceEntrants(unfinished));
        dispatch(actions.updateAboutFinishRaceEntrants(preFinished));
        dispatch(actions.updateCompleteFinishRaceEntrants(finished));
      }
    } catch (error) {
      dispatch(actions.updateInitialFinishRaceEntrants([]));
      dispatch(actions.updateAboutFinishRaceEntrants([]));
      dispatch(actions.updateCompleteFinishRaceEntrants([]));
      console.error("Failed to load session entrants:", error);
    } finally {
      // Keep the loader up briefly after the data dispatches so AG Grid finishes
      // painting the new rows before it disappears (avoids an empty-grid flash).
      loaderHideTimer.current = setTimeout(() => setSessionLoading(false), 300);
    }
  };

  // ── Load per-fleet start times (Start Time drawer) ──────────────────────────
  const mapToGridRow = (obj) => ({
    id: obj.raceSessionDetailId,
    fleet: obj.fleet?.fleet,
    hr: obj.startTimeHH,
    min: obj.startTimeMM,
    sec: obj.startTimeSS,
  });

  const fetchFleetDetails = async (sessionId) => {
    if (!sessionId || sessionId <= 0) {
      setFleetDetails([]);
      setRacePayload({ raceSessionsDetailList: [] });
      return;
    }
    try {
      const enc = encodeURIComponent(encrypt(JSON.stringify(sessionId)));
      const response = await axiosInstance.get(
        "api/clubs/FinishTime/update?raceSession=" + enc,
      );
      const data = response.data.data;
      setRacePayload(data || { raceSessionsDetailList: [] });
      setFleetDetails((data?.raceSessionsDetailList || []).map(mapToGridRow));
    } catch (error) {
      setFleetDetails([]);
      setRacePayload({ raceSessionsDetailList: [] });
      console.error("Failed to load fleet start times:", error);
    } finally {
      setFleetDetailsSession(sessionId);
    }
  };

  // ── Session header callbacks ─────────────────────────────────────────────────
  const handleSetRaceSession = (sessionId, isOvernightRace) => {
    setRaceSession(sessionId);
    setIsOnRace(!!isOvernightRace);
    setSnWithAlpha(true);
    setOnWater(false);
    // Place mode v2: both tabs are available for place sessions too —
    // Manual Times is the default everywhere.
    setMode("manual");
    loadSessionEntrants(sessionId);
    fetchFleetDetails(sessionId);

  };

  // Real-time/Manual toggle repaints both (large) grids — briefly hold the
  // loader overlay so that repaint isn't seen as a glitch. (Only for the user
  // toggle; the session-change path above has its own loader via
  // loadSessionEntrants and calls setMode directly.)
  const handleModeChange = useCallback((newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    if (loaderHideTimer.current) clearTimeout(loaderHideTimer.current);
    setSessionLoading(true);
    loaderHideTimer.current = setTimeout(() => setSessionLoading(false), 1000);
  }, [mode]);

  const openDrawer = (panel) => {
    setDrawerPanel(panel);
    setDrawerSeq((s) => s + 1);
    setDrawerOpen(true);
  };

  // ── Deep link: ?tab=start | ?tab=manual ─────────────────────────────────────
  // Race Overview links here as
  //   /raceday/finish-time-entry?id=…&dateVal=…&raceSessionId=…&tab=start|manual
  // "start" opens the Start Time drawer, "manual" selects the Manual tab.
  //
  // Neither can be applied at mount. FinishTimeHeaderBox auto-selects the session
  // from id/dateVal/raceSessionId, and handleSetRaceSession then resets the mode
  // to the default — landing AFTER mount, so an initial value would be
  // overwritten. Both therefore wait for that session to finish loading.
  // (?tab=manual is a no-op now that Manual is the default, but it is kept so
  // the Race Overview links keep working if the default ever changes back.)
  //
  // "start" waits on fleetDetailsSession too, so the drawer is never rendered
  // against empty start-time data. "manual" deliberately does not: start times
  // are irrelevant to the manual grid, and gating on them would stall the tab if
  // that fetch is slow or fails. setMode is called directly rather than
  // handleModeChange — loadSessionEntrants already shows the loader, so its extra
  // 1s hold would only add lag.
  //
  // ONE-SHOT: a later session change (or the user closing the drawer) must not
  // re-trigger either. Only one tab value can be present, so a single ref covers
  // both.
  const deepLinkTabHandledRef = useRef(false);
  React.useEffect(() => {
    if (deepLinkTabHandledRef.current) return;
    const tab = (
      new URLSearchParams(window.location.search).get("tab") || ""
    ).toLowerCase();
    if (tab !== "start" && tab !== "manual") return;
    if (!raceSession || raceSession <= 0 || sessionLoading) return;
    if (tab === "start" && fleetDetailsSession !== raceSession) return;
    deepLinkTabHandledRef.current = true;
    if (tab === "start") openDrawer("start");
    else setMode("manual");
  }, [raceSession, sessionLoading, fleetDetailsSession]);

  // ── Process-flag polling: lock the UI while results are being processed ──────
  const getRaceSessionStatus = async (sessionId) => {
    if (!sessionId || sessionId <= 0) return;
    try {
      const enc = encodeURIComponent(encrypt(JSON.stringify(sessionId)));
      const res = await axiosInstance.get(
        "api/clubs/FinishTime/flag?raceSession=" + enc,
      );
      setProcessFlag(!!res.data.data);
    } catch (e) {
      setProcessFlag(false);
    }
  };

  // ── Process Results ──────────────────────────────────────────────────────────
  // Process Results click → validate, then open the "Confirm Start Times" dialog.
  // The actual processing only runs once the user presses Proceed in the dialog.
  const handleProcessResultsClick = useCallback(() => {
    if (
      completeFinishRaceEntrants.some(
        (entrant) => !entrant.isAbandon && (entrant.editable === true || entrant.editable === 1),
      )
    ) {
      handleSnackbarChange(
        "The highlighted finish times are invalid or missing. Please review and correct all finish times before processing results. Results cannot be processed until all times are valid.",
        "error",
        true,
      );
      return;
    }
    // Place series: every finished row must carry a place OR a DN (old-screen
    // rule, enforced there at entry time). Safety net in case a row slipped
    // through with neither. Abandoned rows are exempt (their DN is implicit).
    if (
      isPlace &&
      completeFinishRaceEntrants.some(
        (r) => !r.isAbandon && !r.place && !(r.dn || "").trim(),
      )
    ) {
      handleSnackbarChange("Please define either place or DN.", "error", true);
      return;
    }
    setConfirmOpen(true);
  }, [completeFinishRaceEntrants, isPlace, handleSnackbarChange]);

  const runProcessResults = useCallback(async () => {
    try {
      setProcessFlag(true);
      const mapToPayload = (obj) => ({
        raceSessionId: raceSession,
        sessionEntrantId:
          obj.status !== undefined ? obj.sessionEntrantId || "" : obj.id || "",
        place: 0,
        sailNum: obj.sailNum,
        hr: "00",
        mm: "00",
        ss: "00",
        date: sessionDate,
        dn: "DNC",
        fleet: obj.fleetName,
      });

      const payloadFirst = unfinishedEntrants
        .concat(aboutFinishRaceEntrants)
        .filter((evt) => !evt.editable)
        .map(mapToPayload);

      const calcResponse = await axiosInstance.post(
        "api/HC/CalculateHandicap/" + raceSession,
        payloadFirst,
      );

      const calcPayload = calcResponse.data.data;
      if (calcPayload?.length > 0) {
        await axiosInstance.post("api/HC/CalculateHandicap", calcPayload);
        handleSnackbarChange("Results Processed Successfully", "success", true);
        // Re-sync from the server so About-To-Finish/Unfinished reflect the update
        await loadSessionEntrants(raceSession);
      } else {
        throw new Error("Error occurred in publishing Results");
      }
    } catch (error) {
      console.error("Process results error:", error);
      handleSnackbarChange(
        error?.response?.data?.message ||
          error?.message ||
          "Error processing results",
        "error",
        true,
      );
    } finally {
      setProcessFlag(false);
    }
  }, [
    unfinishedEntrants,
    aboutFinishRaceEntrants,
    raceSession,
    sessionDate,
    handleSnackbarChange,
    loadSessionEntrants,
  ]);

  // Confirm Start Times dialog actions
  const handleConfirmProceed = () => {
    runProcessResults();
    setConfirmOpen(false);
  };
  const handleConfirmUpdateStartTime = () => {
    setConfirmOpen(false);
    openDrawer("start");
  };

  React.useEffect(() => {
    if (!raceSession || raceSession <= 0) {
      setProcessFlag(false);
      return;
    }
    getRaceSessionStatus(raceSession);
    const id = setInterval(() => getRaceSessionStatus(raceSession), 5000);
    return () => clearInterval(id);
  }, [raceSession]);

  // ── CSV Import / Export (ported from the old FinishRaceManager) ──────────────
  const makePayloadForUpdate = (obj) => {
    const isCapturedNow = obj.editable;
    // Phantom rows (unknown sail — no boatName) carry a fake local id like
    // "phantom-1". Sending that to the backend causes a 400/500 error, so
    // always use "" for unknown-sail entries so the backend treats them as new.
    const isNew = !obj.boatName;
    return {
      raceSessionId: raceSession,
      sessionEntrantId: obj.status !== undefined
          ? isCapturedNow
            ? ""
            : obj.sessionEntrantId
          : isCapturedNow
            ? ""
            : isNew ? "" : obj.id,
      place: obj.position,
      sailNum: obj.sailNum,
      hr: String(obj.hr).padStart(2, "0"),
      mm: String(obj.min).padStart(2, "0"),
      ss: String(obj.sec).padStart(2, "0"),
      date: sessionDate,
      dn: obj.dn,
      fleet: obj.fleetName,
    };
  };

  // Returns whether the save actually succeeded, so importCsvText can decide
  // whether the "N imported successfully" toast is still true to show.
  const saveRows = async (data, finishedRows, abandonedRows = []) => {
    try {
      const response = await axiosInstance.post(
        "api/clubs/FinishTime/list",
        data,
      );
      if (response.data.success && Array.isArray(response.data.data)) {
        const editableMap = new Map(
          response.data.data.map((item) => [item.sailNum, item.editable]),
        );
        const updated = finishedRows.map((e) =>
          editableMap.has(e.sailNum)
            ? { ...e, editable: editableMap.get(e.sailNum) }
            : e,
        );
        // Re-append ABN entrants that were retained outside the CSV import
        dispatch(actions.updateCompleteFinishRaceEntrants([...updated, ...abandonedRows]));
      }
      return true;
    } catch (error) {
      handleSnackbarChange("Error occured while importing.", "error", true);
      return false;
    }
  };

  const importCsvText = async (text) => {
    const csvHeader = text
      .slice(0, text.indexOf("\n"))
      .split(",")
      .map((h) => h.trim().replace(/\r$/, ""));

    if (
      (!isPlace &&
        (!csvHeader.includes("HR") ||
          !csvHeader.includes("MN") ||
          !csvHeader.includes("SC"))) ||
      !csvHeader.includes(caseOTBEntrant ? "Class" : "Boat") ||
      !csvHeader.includes("Sail No") ||
      !csvHeader.includes("DidNot")
    ) {
      handleSnackbarChange("Please select a valid CSV File.", "error", true);
      return;
    }

    const csvRows = text
      .slice(text.indexOf("\n") + 1)
      .split("\n")
      .filter((row) => row.trim() !== "");
    if (csvRows.length === 0) {
      handleSnackbarChange("No Rows Found.", "error", true);
      return;
    }

    const times = csvRows.map((line) => {
      const values = line.split(",");
      return csvHeader.reduce((obj, header, index) => {
        obj[header.replace(" ", "")] = values[index]?.trim().replace(/\r$/, "");
        return obj;
      }, {});
    });

    // Combine every entrant back into one pool with cleared times, then re-match.
    const resetTime = (e) => ({
      ...e,
      hr: "00",
      min: "00",
      sec: "00",
      dn: "",
      place: "",
      position: "",
      finishDate: "",
      editable: false,
    });
    const pool = [
      ...unfinishedEntrants,
      ...completeFinishRaceEntrants
        // Phantom rows (invalid/unmatched sail number, no real boatName —
        // see isUnknownSail) have no real entrant behind them. The "---"
        // check here was stale (nothing in this screen's data model ever
        // sets boatName/fleetName to that string; phantom rows just have an
        // empty boatName), so it silently let phantoms through into the pool
        // — CSV import couldn't match their garbage sail number, so they
        // fell through into "unfinished" instead of being discarded.
        .filter((e) => e.sailNum && !isUnknownSail(e))
        .map(resetTime),
      ...aboutFinishRaceEntrants.filter((e) => e.sailNum).map(resetTime),
    ];

    let validTimes = [];
    let counter = 0;
    times.forEach((i) => {
      const basicsOk =
        i.SailNo &&
        i.SailNo !== "" &&
        (caseOTBEntrant ? i.Class : i.Boat) &&
        i.Fleet &&
        // Place series: require a valid positive Place unless a penalty is set
        // (matches the old screen's import rule)
        (!isPlace ||
          i.Place === undefined ||
          (i.Place?.trim() !== "" && isNumeric(i.Place) && +i.Place > 0) ||
          (i.DidNot && i.DidNot.trim() !== "")) &&
        (i.HR == undefined || isNumeric(i.HR) || i.HR === "") &&
        (i.MN == undefined || isNumeric(i.MN) || i.MN === "") &&
        (i.SC == undefined || isNumeric(i.SC) || i.SC === "") &&
        (!i.DidNot ||
          i.DidNot.trim() === "" ||
          allPenalties
            .map((x) => x.penaltyName)
            .includes(i.DidNot.trim().toUpperCase()));
      if (!basicsOk) return;

      const row = pool.find(
        (x) =>
          x.sailNum &&
          x.sailNum.toUpperCase() === i.SailNo.toUpperCase() &&
          (x.boatName?.toUpperCase() === i.Boat?.toUpperCase() ||
            (caseOTBEntrant &&
              x.boatName?.toUpperCase() === i.Class?.toUpperCase())),
      );
      if (!row) return;
      if (
        validTimes.some(
          (f) => f.sailNum.toUpperCase() === i.SailNo.toUpperCase(),
        )
      )
        return;

      counter++;
      const dn = i.DidNot ? i.DidNot.toUpperCase() : "";
      const newRow = {
        ...row,
        position: isPlace ? i.Place : i.position ? i.position : counter,
        place: isPlace ? i.Place : counter,
        dn,
      };
      if (dn !== "" && !TIME_PENALTIES.includes(dn)) {
        newRow.hr = "00";
        newRow.min = "00";
        newRow.sec = "00";
      } else {
        newRow.hr = i.HR
          ? i.HR.length === 1
            ? "0" + i.HR
            : +i.HR > 23
              ? "23"
              : i.HR
          : "00";
        newRow.min = i.MN
          ? i.MN.length === 1
            ? "0" + i.MN
            : +i.MN > 59
              ? "59"
              : i.MN
          : "00";
        newRow.sec = i.SC
          ? i.SC.length === 1
            ? "0" + i.SC
            : +i.SC > 59
              ? "59"
              : i.SC
          : "00";
      }
      validTimes.push(newRow);
    });

    if (isPlace) {
      // Descending by place — old-screen import order (race-manager.jsx),
      // which also matches this grid's highest-on-top convention.
      validTimes = [...validTimes].sort(
        (a, b) => (+b.place || 0) - (+a.place || 0),
      );
    }

    const matchedIds = new Set(validTimes.map((v) => v.id));
    const unfinished = pool.filter(
      (r) => !matchedIds.has(r.id) && !r.isAbandon,
    );
    // ABN entrants not in the CSV stay in finished with their original data
    const retainedAbn = completeFinishRaceEntrants.filter(
      (r) => !matchedIds.has(r.id) && r.isAbandon,
    );

    dispatch(actions.updateInitialFinishRaceEntrants(unfinished));
    dispatch(actions.updateAboutFinishRaceEntrants([]));
    dispatch(actions.updateCompleteFinishRaceEntrants([...validTimes, ...retainedAbn]));

    const saved = await saveRows(
      validTimes.map(makePayloadForUpdate),
      validTimes,
      retainedAbn,
    );
    if (saved) {
      handleSnackbarChange(
        `${validTimes.length} out of ${times.length} valid entrants imported successfully`,
        "success",
        true,
      );
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSessionLoading(true);
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          await importCsvText(String(ev.target?.result || ""));
        } finally {
          setSessionLoading(false);
        }
      };
      reader.onerror = () => setSessionLoading(false);
      reader.readAsText(file);
    }
    e.target.value = "";
  };

  const downloadSampleCSV = () => {
    let columns = isPlace
      ? ["Boat", "Sail No", "Fleet", "Place", "DidNot"]
      : ["Boat", "Sail No", "Fleet", "HR", "MN", "SC", "DidNot"];
    if (caseOTBEntrant) columns[0] = "Class";
    const csvContent = `data:text/csv;charset=utf-8,${columns.join(",")}\n`;
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "FinishTimeImportSample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export the current Finished rows to CSV (same data export as the old
  // real-time / manual capture screens; header format matches the importer).
  const exportFinishTimes = () => {
    if (
      !completeFinishRaceEntrants ||
      completeFinishRaceEntrants.length === 0
    ) {
      handleSnackbarChange(
        "No data in Finished Table to export.",
        "error",
        true,
      );
      return;
    }

    // Prefer reading rows from the AG Grid API so the exported order matches
    // whatever sorting the user has applied. Fall back to Redux data if the
    // grid API is not yet available.
    let sourceRows = completeFinishRaceEntrants;
    const gridApi = finishedGridRef.current?.api;
    if (gridApi) {
      const displayed = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        if (node.data) displayed.push(node.data);
      });
      if (displayed.length > 0) sourceRows = displayed;
    }
    // Place mode exports Boat/Class, Sail No, Fleet, Place, DidNot — no time
    // columns (old-screen export parity, app_components_finish-time.jsx).
    const filteredItems = isPlace
      ? sourceRows.map(
          ({ boatName, sailNum, fleetName, place, dn }) => ({
            boatName,
            sailNum,
            fleetName,
            place,
            dn,
          }),
        )
      : sourceRows.map(
          ({ boatName, sailNum, fleetName, hr, min, sec, dn }) => ({
            boatName,
            sailNum,
            fleetName,
            hr,
            min,
            sec,
            dn,
          }),
        );

    const csvHeader =
      Object.keys(filteredItems[0])
        .map((key) => {
          let header = key.charAt(0).toUpperCase() + key.slice(1);
          if (caseOTBEntrant && key === "boatName") {
            header = "Class";
          } else {
            header = header
              .replace("Name", "")
              .replace("SailNum", "Sail No")
              .replace("Hr", "HR")
              .replace("Min", "MN")
              .replace("Sec", "SC")
              .replace("Dn", "DidNot");
          }
          return header;
        })
        .join(",") + "\n";

    const csvBody = filteredItems
      .map((row) => Object.values(row).join(","))
      .join("\n");

    const today = new Date();
    const date =
      today.getFullYear() +
      "-" +
      (today.getMonth() + 1) +
      "-" +
      today.getDate();

    const hiddenElement = document.createElement("a");
    hiddenElement.href =
      "data:text/csv;charset=utf-8," + encodeURI(csvHeader + csvBody);
    hiddenElement.target = "_blank";
    hiddenElement.download = "FinishTimes-" + date + ".csv";
    document.body.appendChild(hiddenElement);
    hiddenElement.click();
    document.body.removeChild(hiddenElement);
  };

  return (
    <>
      <CustomizedSnackbars
        severity={snackbar.severity}
        open={open}
        setShowToast={setOpen}
        text={snackbar.text}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Box
        className="inner_wrapper_pages finish-times-page"
        sx={
          isFullscreen
            ? {
                position: "fixed",
                inset: 0,
                zIndex: 1250,
                bgcolor: "#fff",
                overflow: "auto",
                p: { xs: 1, sm: 2 },
              }
            : undefined
        }
      >
        {/* Fullscreen: live clock pinned flush to the top-right corner. position:fixed
            anchors it to the viewport edge (absolute would sit inside the wrapper's
            16px padding). pointerEvents:none → never blocks the action icons beneath. */}
        {isFullscreen && showClock && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: "1px",
              zIndex: 1300,
              pointerEvents: "none",
            }}
          >
            <LiveClock big />
          </Box>
        )}

        {/* Page heading with the live clock on its right (in fullscreen the heading
            is hidden and the clock is the corner overlay above instead). */}
        {!isFullscreen && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "left",
              flexWrap: "nowrap",
              gap: 1,
            }}
          >
            <InnerPageheading headerName="Finish Time Entry" />
            {showClock && (
              <Box
                sx={{
                  pr: { xs: 1, sm: 0 },
                  flexShrink: 0,
                  position: "relative",
                  left: "-15px",
                }}
              >
                <LiveClock big />
              </Box>
            )}
          </Box>
        )}

        <Box
          className="InnerPage_area"
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            padding: "16px",
            ...(isFullscreen && { height: "100% !important" }),
          }}
        >
          {/* ── Single compact toolbar: selectors (left) + controls (right) ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            {/* Left: compact Event / Date / Session selectors + action buttons */}
            <FinishTimeEntryHeaderBox
              setRaceSessions={handleSetRaceSession}
              setRaceSessionNotes={() => {}}
              setIsPlace={(flag) => setIsPlace(flag)}
              setPenalties={(pen) => setAllPenalties(pen)}
              setEventId={(ev) => setEventId(ev?.eventId ?? ev)}
              setDate={(d) => setSessionDate(d)}
              setCaseOTBEntrant={(v) => setCaseOTBEntrant(v)}
              showActions={raceSession > 0 && !processFlag && !sessionLoading}
              hasEntrants={(
                (unfinishedEntrants?.length ?? 0) +
                (completeFinishRaceEntrants?.length ?? 0)
              ) > 0}
              hasFinishedEntrants={(completeFinishRaceEntrants?.length ?? 0) > 0}
              onOpenDrawer={openDrawer}
              onImportClick={(e) => setImportAnchorEl(e.currentTarget)}
              onExport={exportFinishTimes}
              onToggleFullscreen={toggleFullscreen}
              isFullscreen={isFullscreen}
            />

            {/* Import Popover (anchored from header box import button) */}
            {raceSession > 0 && !processFlag && !sessionLoading && (
              <>
                <Popover
                  id={importAnchorEl ? "select_source" : undefined}
                  open={Boolean(importAnchorEl)}
                  anchorEl={importAnchorEl}
                  onClose={closeImportPopup}
                  disableScrollLock
                  className="select_source_main"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <div className="select_source_div" style={{ width: 368 }}>
                    <FormLabel
                      className="ty-uppercase-label mb-0"
                      sx={{
                        display: "block",
                        width: "100%",
                        mb: 0,
                        pl: "0.5rem",
                        pt: "0.5rem",
                      }}
                    >
                      Select Source
                    </FormLabel>
                    <Box className="cst_hr" />
                    <Box
                      sx={{
                        p: "0 16px 16px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box mt={2}>
                        {!selectedFile ? (
                          <Box
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const f = e.dataTransfer.files?.[0];
                              if (f) setSelectedFile(f);
                            }}
                            style={{ cursor: "pointer" }}
                            className="file_upload_div_section"
                          >
                            <input
                              type="file"
                              style={{ display: "none" }}
                              id="nftFileInput"
                              accept=".csv"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) setSelectedFile(f);
                                e.target.value = "";
                              }}
                            />
                            <label
                              htmlFor="nftFileInput"
                              className="upload_btn"
                            >
                              <Button variant="text" component="div">
                                <ul
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "10px",
                                    listStyle: "none",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <li
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <SaveAltIcon />
                                  </li>
                                  <li>Drag and drop or click to select file</li>
                                </ul>
                              </Button>
                            </label>
                          </Box>
                        ) : (
                          <Box className="file_uploading">
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "16px",
                                  alignItems: "center",
                                }}
                              >
                                <div className="csv_file">
                                  <img
                                    src="/assets/icons/csv_icon.svg"
                                    alt="csv"
                                    style={{ maxWidth: "100%" }}
                                  />
                                </div>
                                <Typography
                                  variant="body1"
                                  sx={{ wordBreak: "break-all" }}
                                >
                                  {selectedFile.name}
                                </Typography>
                              </Box>
                            </Box>
                            <Box>
                              <ClearIcon
                                className="clearall_btn"
                                onClick={() => setSelectedFile(null)}
                                sx={{
                                  display: "flex",
                                  padding: "0px",
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box mt={2} mb={2}>
                        <Accordion className="cst_accordion">
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="nft-note-content"
                            id="nft-note-header"
                          >
                            <InfoOutlinedIcon className="information_icon" />
                            <Typography className="important_notes_heading">
                              Important Note
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{ maxHeight: "115px", overflowY: "scroll" }}
                          >
                            <List>
                              {caseOTBEntrant && (
                                <ListItem>
                                  “CLASS NAME” - must be a valid CLASS NAME that
                                  matches an existing entrant.
                                </ListItem>
                              )}
                              <ListItem>
                                “HR” - must be a valid number between 0 and 23.
                              </ListItem>
                              <ListItem>
                                “MIN” - must be a valid number between 0 and 60.
                              </ListItem>
                              <ListItem>
                                “SEC” - must be a valid number between 0 and 60.
                              </ListItem>
                              <ListItem>
                                “DidNot” - must include a valid penalty code as
                                per the RRS.
                              </ListItem>
                              <ListItem>
                                “Sail No.” - must be a valid sail number that
                                matches an existing entrant.
                              </ListItem>
                              <ListItem>
                                “Boat Name” - must be a valid boat name that
                                matches an existing entrant.
                              </ListItem>
                              <ListItem>
                                “Fleet” - must be a valid fleet that matches an
                                existing entrant.
                              </ListItem>
                              <ListItem>
                                “Place” - must be a valid numeric value.
                              </ListItem>
                            </List>
                          </AccordionDetails>
                        </Accordion>

                        <Box
                          sx={{
                            textAlign: "center",
                            margin: "10px auto",
                            maxWidth: "90%",
                          }}
                        >
                          <Button
                            size="small"
                            className="ty_secondary_btn"
                            onClick={downloadSampleCSV}
                          >
                            Download Sample CSV{" "}
                            <DownloadIcon sx={{ height: "20px" }} />
                          </Button>
                        </Box>
                      </Box>

                      <Box className="cst_hr" sx={{ marginTop: "0px" }} />

                      <div className="button_group_div">
                        <Grid
                          container
                          justifyContent="flex-end"
                          sx={{ display: "flex", gap: "8px" }}
                        >
                          <Grid item>
                            <Button
                              className="ty_secondary_btn"
                              onClick={closeImportPopup}
                            >
                              Cancel
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              className="ty_primary_btn"
                              disabled={!selectedFile}
                              onClick={handleImportSelected}
                            >
                              Import
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    </Box>
                  </div>
                </Popover>
              </>
            )}
          </Box>

          {/* Guard: no session selected */}
          {!raceSession && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning">No Session Found.</Alert>
            </Box>
          )}

          {/* Main UI */}
          {raceSession > 0 && (
            <Box sx={{ position: "relative" }}>
              {sessionLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255, 255, 255, 0.65)",
                  }}
                >
                  <CircularProgress size={48} />
                </Box>
              )}
              {processFlag ? (
                <Card sx={{ mt: 1, position: "relative" }}>
                  <CardContent>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 8,
                        color: "var(--ty-primary-hovercolor, #2b598d)",
                      }}
                    >
                      <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                        Please wait — results are already being processed…
                      </Typography>
                      <Box my={2}>
                        <CircularProgress size={56} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <FinishTimeBody
                  session={raceSession}
                  mode={mode}
                  place={isPlace}
                  penalties={allPenalties}
                  activateSnackbarTab={handleSnackbarChange}
                  raceSessionsDate={sessionDate}
                  event={eventId}
                  overnight={isOnRace}
                  caseOTBEntrant={caseOTBEntrant}
                  onWater={onWater}
                  snWithAlpha={snWithAlpha}
                  setOnWater={setOnWater}
                  setSnWithAlpha={setSnWithAlpha}
                  setMode={handleModeChange}
                  currentMode={mode}
                  isPlace={isPlace}
                  processFlag={processFlag}
                  onProcessResults={handleProcessResultsClick}
                  isFullscreen={isFullscreen}
                  fleetDetails={fleetDetails}
                  finishedGridRef={finishedGridRef}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Right slide-over sidebar: Start Time + Session Notes ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        disableScrollLock
        keepMounted
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
        ModalProps={{ disableScrollLock: true, disableEnforceFocus: true }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440, md: 480 },
            maxWidth: "100%",
            willChange: "transform",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--ty-font-family)",
              fontSize: "var(--font_size16)",
              fontWeight: 700,
              letterSpacing: "2.4px",
              textTransform: "uppercase",
              color: "var(--ty-primary-color, #356dad)",
            }}
          >
            {drawerPanel === "start" ? "Starts" : "Session Notes"}
          </Typography>
          <IconButton size="small" onClick={() => setDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 2.5, overflowY: "auto" }}>
          {drawerPanel === "start" ? (
            <StartTime
              key={drawerSeq}
              fleetDetails={fleetDetails}
              racePayload={racePayload}
              onStartTimeUpdate={(updated) => setFleetDetails(updated)}
            />
          ) : (
            <SessionNotesTab
              sessionId={raceSession}
              event={eventId}
              raceSessionsDate={sessionDate}
            />
          )}
        </Box>
      </Drawer>

      {/* Confirm Start Times before processing results */}
      <StartTimeConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onProceed={handleConfirmProceed}
        onUpdateStartTime={handleConfirmUpdateStartTime}
        fleetDetails={fleetDetails}
        processing={processFlag}
      />
    </>
  );
}
